import * as React from 'react'

import { deriveContentState } from './deriveContentState'
import { useGlobalSearchNavigation } from '../../../../hooks/useGlobalSearchNavigation'
import { type GlobalSearchRecentItem } from '../../../../value-objects/GlobalSearchRecentItem'
import { type GlobalSearchResultItem } from '../../../../value-objects/GlobalSearchResultItem'
import { type ContentStateEnum } from '../../enums/ContentStateEnum'
import { useMutateAddGlobalSearchRecentItem } from '../../models/useMutateAddGlobalSearchRecentItem'
import { useQueryGlobalSearchRecentItems } from '../../models/useQueryGlobalSearchRecentItems'
import { useQueryGlobalSearchResults } from '../../models/useQueryGlobalSearchResults'

interface IUseGetGlobalSearchResultsPresenterResult {
  commands: {
    onQueryChange: (value: string) => void
    onSubmit: () => void
    onRecentItemClick: (item: GlobalSearchRecentItem) => void
    onResultItemClick: (item: GlobalSearchResultItem) => void
  }
  data: {
    query: string
    recentItems: GlobalSearchRecentItem[]
    resultItems: GlobalSearchResultItem[]
    inputRef: React.RefObject<HTMLInputElement>
  }
  states: {
    showEnterHint: boolean
    contentState: Nullable<ContentStateEnum>
    showTruncatedNotice: boolean
  }
}

export const useGetGlobalSearchResultsPresenter = (
  onClose: () => void
): IUseGetGlobalSearchResultsPresenterResult => {
  const [query, setQuery] = React.useState('')
  const [submittedQuery, setSubmittedQuery] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const { navigate } = useGlobalSearchNavigation()
  const { recentItems } = useQueryGlobalSearchRecentItems()
  const { addRecentItem } = useMutateAddGlobalSearchRecentItem()
  const {
    data: searchResult,
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = useQueryGlobalSearchResults(submittedQuery)

  const resultItems = searchResult?.items ?? []

  const showEnterHint = query !== submittedQuery && query.length > 0

  const contentState: Nullable<ContentStateEnum> = deriveContentState({
    hasSubmittedQuery: submittedQuery !== '',
    hasRecentItems: recentItems.length > 0,
    isSearchLoading,
    hasResultItems: resultItems.length > 0,
    isSearchSuccess:
      !isSearchLoading && !isSearchError && searchResult !== undefined,
    isUnsupportedQuery: searchResult?.isUnsupportedQuery ?? false,
    isSearchError,
  })

  // Tab gate (selectedTab === ALL) is applied in US-7
  const showTruncatedNotice = searchResult?.isTruncated ?? false

  const onQueryChange = React.useCallback((value: string) => {
    setQuery(value)
    if (value === '') {
      setSubmittedQuery('')
    }
  }, [])

  const onSubmit = React.useCallback(() => {
    setSubmittedQuery((prev) => (prev === query ? prev : query))
  }, [query])

  const onRecentItemClick = React.useCallback(
    (item: GlobalSearchRecentItem) => {
      const status = 'status' in item.metadata ? item.metadata.status : null
      addRecentItem({
        type: item.type,
        uuid: item.uuid,
        id: item.id,
        metadata: item.metadata,
      })
      onClose()
      navigate({ type: item.type, uuid: item.uuid, id: item.id, status })
    },
    [navigate, addRecentItem, onClose]
  )

  const onResultItemClick = React.useCallback(
    (item: GlobalSearchResultItem) => {
      const status = 'status' in item.metadata ? item.metadata.status : null
      addRecentItem({
        type: item.type,
        uuid: item.uuid,
        id: item.id,
        metadata: item.metadata,
      })
      onClose()
      navigate({ type: item.type, uuid: item.uuid, id: item.id, status })
    },
    [navigate, addRecentItem, onClose]
  )

  return {
    commands: {
      onQueryChange,
      onSubmit,
      onRecentItemClick,
      onResultItemClick,
    },
    data: {
      query,
      recentItems,
      resultItems,
      inputRef,
    },
    states: {
      showEnterHint,
      contentState,
      showTruncatedNotice,
    },
  }
}
